import { HedgePosition } from '../../services/hedgesService';
import { StackedBarChartElement } from '../../sp-types/dashboardTypes';
import { Material } from '../../sp-types/materialType';
import { MonthYear } from '../../utils/MonthYear/monthYear';
import { GENERAL_ID } from '../../components/Stores/exposureDataStore';
import { toNumber, groupByFormula } from '../../utils/positionUtils';

/** Lighten a hex color by mixing with white. amount=0→original, amount=1→white */
function lightenColor(hex: string, amount: number): string {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  const lr = Math.round(r + (255 - r) * amount);
  const lg = Math.round(g + (255 - g) * amount);
  const lb = Math.round(b + (255 - b) * amount);
  return `#${lr.toString(16).padStart(2, '0')}${lg.toString(16).padStart(2, '0')}${lb.toString(16).padStart(2, '0')}`;
}

/**
 * Transform Postgres hedge positions into Dashboard stacked bar chart format
 * Similar to how demandAdapter transforms demand positions
 */

const getMonthYear = (dateString: string): MonthYear => {
  const [year, month] = dateString.split('-').map(Number);
  return { year, month };
};

const getMonthKey = (date: MonthYear): string => `${date.year}-${date.month}`;

/**
 * Aggregate hedges by maturity month - returns both quantity and monetary values
 * Always aggregates by maturity month only (no spreading across months)
 */
const aggregateHedgesByMonth = (
  hedges: HedgePosition[],
  pricesMap: Record<string, number>,
): { quantities: Record<string, number>; monetary: Record<string, number> } => {
  const monthlyQuantities: Record<string, number> = {};
  const monthlyMonetary: Record<string, number> = {};

  hedges.forEach((hedge) => {
    const quantity = toNumber(hedge.hedged_quantity_mt);
    
    // Account for position direction: long = +, short = -
    const directedQuantity = hedge.position_side === 'short' ? -quantity : quantity;
    
    // Always use spot price for consistent valuation across all views
    const price = pricesMap[hedge.formula_code] || 0;
    const monetaryValue = directedQuantity * price;
    
    // Aggregate by maturity month
    const maturityMonth = getMonthYear(hedge.maturity_date);
    const key = getMonthKey(maturityMonth);
    
    monthlyQuantities[key] = (monthlyQuantities[key] || 0) + directedQuantity;
    monthlyMonetary[key] = (monthlyMonetary[key] || 0) + monetaryValue;
  });

  return { quantities: monthlyQuantities, monetary: monthlyMonetary };
};

/**
 * Fill in months based on exposure horizon (configurable, default 12 months)
 */
const fillMonthsArray = (
  monthlyValues: Record<string, number>,
  horizonMonths: number = 12
): { date: MonthYear; value: number }[] => {
  const now = new Date();
  const result: { date: MonthYear; value: number }[] = [];
  
  for (let i = 0; i < horizonMonths; i++) {
    const date = new Date(now.getFullYear(), now.getMonth() + i, 1);
    const monthYear: MonthYear = {
      month: date.getMonth() + 1,
      year: date.getFullYear(),
    };
    const key = getMonthKey(monthYear);
    result.push({
      date: monthYear,
      value: monthlyValues[key] || 0,
    });
  }
  
  return result;
};

/**
 * Main adapter function: Transform hedges to stacked bar chart elements
 * Always returns both quantity and monetary arrays
 * @param isQuantity - for backward compatibility with valueArray/sumValue
 */
export const transformHedgesToStackedBar = (
  hedges: HedgePosition[] | undefined,
  selectedFormulaId: string,
  materialsMap: Record<string, Material>,
  colorsMap: Record<string, string>,
  isQuantity: boolean,
  pricesMap: Record<string, number>,
  horizonMonths: number = 12,
): StackedBarChartElement[] => {
  if (!hedges || hedges.length === 0) {
    return [];
  }

  const hedgesByFormula = groupByFormula(hedges);
  
  // Filter by selected formula if not general mode
  const isGeneralMode = selectedFormulaId === GENERAL_ID;
  const formulasToShow = isGeneralMode
    ? Object.keys(hedgesByFormula)
    : Object.keys(hedgesByFormula).filter(f => f === selectedFormulaId);

  const result: StackedBarChartElement[] = [];

  formulasToShow.forEach((formulaCode) => {
    const formulaHedges = hedgesByFormula[formulaCode] || [];

    // Split into futures and options
    const futuresHedges = formulaHedges.filter(h => h.instrument_type !== 'option');
    const optionsHedges = formulaHedges.filter(h => h.instrument_type === 'option');

    const baseColor = colorsMap[formulaCode] || '#6b7280';

    // Futures bar
    if (futuresHedges.length > 0 || optionsHedges.length === 0) {
      const { quantities, monetary } = aggregateHedgesByMonth(futuresHedges, pricesMap);
      const quantityArray = fillMonthsArray(quantities, horizonMonths);
      const monetaryArray = fillMonthsArray(monetary, horizonMonths);
      const sumQuantity = quantityArray.reduce((sum, item) => sum + toNumber(item.value), 0);
      const sumMonetary = monetaryArray.reduce((sum, item) => sum + toNumber(item.value), 0);

      result.push({
        formulaId: formulaCode,
        fieldName: materialsMap[formulaCode]?.name || formulaCode,
        unit: materialsMap[formulaCode]?.unit,
        price: pricesMap[formulaCode] || 0,
        color: baseColor,
        valueArray: isQuantity ? quantityArray : monetaryArray,
        quantityArray,
        monetaryArray,
        marketMonetaryArray: monetaryArray,
        sumValue: isQuantity ? sumQuantity : sumMonetary,
        sumQuantity,
        sumMonetary,
        sumMarketMonetary: sumMonetary,
        totalWeight: sumQuantity,
      });
    }

    // Options bar (separate color — lighter/hatched variant)
    if (optionsHedges.length > 0) {
      const { quantities, monetary } = aggregateHedgesByMonth(optionsHedges, pricesMap);
      const quantityArray = fillMonthsArray(quantities, horizonMonths);
      const monetaryArray = fillMonthsArray(monetary, horizonMonths);
      const sumQuantity = quantityArray.reduce((sum, item) => sum + toNumber(item.value), 0);
      const sumMonetary = monetaryArray.reduce((sum, item) => sum + toNumber(item.value), 0);

      result.push({
        formulaId: `${formulaCode}__options`,
        fieldName: `${materialsMap[formulaCode]?.name || formulaCode} (Options)`,
        unit: materialsMap[formulaCode]?.unit,
        price: pricesMap[formulaCode] || 0,
        color: lightenColor(baseColor, 0.4),
        valueArray: isQuantity ? quantityArray : monetaryArray,
        quantityArray,
        monetaryArray,
        marketMonetaryArray: monetaryArray,
        sumValue: isQuantity ? sumQuantity : sumMonetary,
        sumQuantity,
        sumMonetary,
        sumMarketMonetary: sumMonetary,
        totalWeight: sumQuantity,
      });
    }
  });

  return result;
};
